<template>
  <div class="exam-wiz">
    <!-- Header -->
    <div class="exam-wiz__head">
      <div>
        <h2 class="exam-wiz__title">
          <i class="material-icons-round">assignment</i>
          새 시험 만들기
        </h2>
        <p class="exam-wiz__sub">단계별로 시험을 구성하고 문제를 배치하세요</p>
      </div>
    </div>

    <!-- Steps -->
    <div class="exam-wiz__steps">
      <div class="exam-wiz__steps-track">
        <div class="exam-wiz__steps-progress" :style="{ width: progressPercentage + '%' }"></div>
      </div>
      <div class="exam-wiz__steps-list">
        <div
          v-for="step in wizardSteps"
          :key="step.id"
          class="exam-wiz__step"
          :class="{
            'is-active': currentStep === step.id,
            'is-done': currentStep > step.id,
            'is-disabled': currentStep < step.id,
          }"
          role="button"
          tabindex="0"
          :aria-disabled="currentStep < step.id"
          @click="currentStep >= step.id ? goToStep(step.id) : null"
          @keydown.enter.stop.prevent="currentStep >= step.id ? goToStep(step.id) : null"
        >
          <div class="exam-wiz__step-icon">
            <i class="material-icons-round">{{ currentStep > step.id ? 'check' : step.icon }}</i>
          </div>
          <div class="exam-wiz__step-content">
            <div class="exam-wiz__step-num">STEP {{ step.id }}</div>
            <div class="exam-wiz__step-title">{{ step.name }}</div>
            <div class="exam-wiz__step-desc">{{ getStepDescription(step.id) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Body -->
    <ACard class="exam-wiz__body">
      <transition name="step-fade" mode="out-in">
        <!-- Step 1: 기본 정보 -->
        <div v-if="currentStep === 1" key="step-1" class="exam-wiz__step-body">
          <h5 class="exam-wiz__step-h">기본 정보</h5>
          <form @submit.prevent="nextStep">
            <div class="p-form-grid p-form-grid--4">
              <div class="p-field" style="grid-column: span 2;">
                <label class="p-field__label">시험명 <span class="p-field__req">*</span></label>
                <input v-model="examData.examName" class="p-input" placeholder="예: 2024년 1학기 중간고사" required />
              </div>
              <div class="p-field">
                <label class="p-field__label">시험 시간 (분) <span class="p-field__req">*</span></label>
                <input v-model.number="examData.duration" type="number" class="p-input" min="10" max="300" placeholder="60" required />
              </div>
              <div class="p-field">
                <label class="p-field__label">타임존 <span class="p-field__req">*</span></label>
                <select v-model="examData.timeZone" class="p-input" required>
                  <option v-for="tz in timeZones" :key="tz" :value="tz">{{ tz }}</option>
                </select>
              </div>
              <div class="p-field" style="grid-column: span 4;">
                <label class="p-field__label">시작 시간 <span class="p-field__req">*</span></label>
                <input v-model="examData.startTime" type="datetime-local" class="p-input" required />
              </div>
              <div class="p-field" style="grid-column: span 4;">
                <label class="p-field__label">설명</label>
                <textarea v-model="examData.description" class="p-input p-input--area" rows="3" placeholder="시험에 대한 간단한 설명을 입력하세요" />
              </div>
            </div>

            <div class="p-field exam-wiz__field-section">
              <label class="p-field__label">시험지 생성 방식 <span class="p-field__req">*</span></label>
              <div class="exam-wiz__mode">
                <label class="exam-wiz__mode-card" :class="{ 'is-active': examData.creationMode === 'tag' }">
                  <input v-model="examData.creationMode" type="radio" value="tag" />
                  <i class="material-icons-round exam-wiz__mode-icon">label</i>
                  <div>
                    <strong>태그로 한 번에</strong>
                    <p>미리 묶어둔 시험 세트(examTags)를 선택해 빠르게 출제</p>
                  </div>
                </label>
                <label class="exam-wiz__mode-card" :class="{ 'is-active': examData.creationMode === 'manual' }">
                  <input v-model="examData.creationMode" type="radio" value="manual" />
                  <i class="material-icons-round exam-wiz__mode-icon">tune</i>
                  <div>
                    <strong>직접 구성</strong>
                    <p>스킬별 문제 수와 난이도를 강사가 직접 설정 (기존 방식)</p>
                  </div>
                </label>
              </div>
            </div>

            <div class="p-form-grid p-form-grid--2 exam-wiz__field-section">
              <div class="p-field">
                <label class="p-field__label">교재 선택 <span class="p-field__req">*</span></label>
                <input v-model="textbookQuery" class="p-input" placeholder="교재 검색 (이름, 바코드)" />
                <select v-model="selectedTextbooks" class="p-input exam-wiz__textbook-select" multiple size="6">
                  <option v-for="tb in filteredTextbooks" :key="tb.barcode" :value="tb.barcode">
                    {{ tb.title }} ({{ tb.barcode }})
                  </option>
                </select>
                <div v-if="selectedTextbooks.length > 0" class="exam-wiz__chips">
                  <span v-for="code in selectedTextbooks" :key="code" class="exam-wiz__chip">
                    {{ availableTextbooks.find(t => t.barcode === code)?.title || code }}
                    <button type="button" class="exam-wiz__chip-x" @click="removeTextbook(code)">
                      <i class="material-icons-round">close</i>
                    </button>
                  </span>
                </div>
              </div>

              <div class="p-field">
                <label class="p-field__label">
                  교재별 출제 단원 범위
                  <span class="exam-wiz__hint-inline">(선택사항 — 미설정 시 전체 단원)</span>
                </label>
                <div v-if="selectedTextbooks.length === 0" class="exam-wiz__empty-hint">
                  교재를 먼저 선택해주세요
                </div>
                <div v-for="code in selectedTextbooks" :key="'unit-' + code" class="exam-wiz__unit-row">
                  <strong>{{ availableTextbooks.find(t => t.barcode === code)?.title || code }}</strong>
                  <input
                    type="text"
                    class="p-input"
                    :value="examData.textBookUnits[code] || ''"
                    placeholder="예: 1-5, 8, 10"
                    @input="examData.textBookUnits = { ...examData.textBookUnits, [code]: $event.target.value }"
                  />
                  <p v-if="parseUnitInput(examData.textBookUnits[code]).length > 0" class="exam-wiz__hint">
                    {{ parseUnitInput(examData.textBookUnits[code]).join(', ') }} 단원
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>

        <!-- Step 2: 설정 -->
        <div v-else-if="currentStep === 2" key="step-2" class="exam-wiz__step-body">
          <h5 class="exam-wiz__step-h">시험 설정</h5>
          <div class="p-form-grid p-form-grid--2">
            <div class="p-field">
              <label class="p-check" :class="{ 'is-on': examData.settings.showResults }">
                <input v-model="examData.settings.showResults" type="checkbox" />
                <span>시험 종료 후 결과 표시</span>
              </label>
            </div>
            <div class="p-field">
              <label class="p-check">
                <input v-model="examData.settings.generateABVersion" type="checkbox" />
                <span>A/B 버전 자동 생성</span>
              </label>
            </div>
            <div class="p-field">
              <label class="p-check">
                <input v-model="examData.settings.faceToFace" type="checkbox" />
                <span>{{ examData.settings.faceToFace ? '대면' : '비대면' }}</span>
              </label>
              <p class="p-field__hint" :class="examData.settings.faceToFace ? 'p-field__hint--warn' : ''">
                {{ examData.settings.faceToFace ? '대면: 시험이 수동으로 시작됩니다' : '비대면: 예정된 시간에 자동으로 시작됩니다' }}
              </p>
            </div>
            <div class="p-field">
              <label class="p-field__label">오디오 재생 제한</label>
              <input v-model.number="examData.settings.audioPlayLimit" type="number" class="p-input" min="1" max="10" placeholder="3" />
              <p class="p-field__hint">학생당 오디오 재생 가능 횟수</p>
            </div>
          </div>
        </div>

        <!-- Step 3: 문제 분포 -->
        <div v-else-if="currentStep === 3" key="step-3" class="exam-wiz__step-body">
          <!-- A모드 -->
          <template v-if="examData.creationMode === 'tag'">
            <h5 class="exam-wiz__step-h">시험 세트 선택 (examTags)</h5>
            <p class="exam-wiz__step-d">
              미리 등록된 태그를 입력하면 해당 세트의 문제로 시험지가 자동 구성됩니다.
              분포가 마음에 들지 않으면 다른 태그로 다시 조회하세요.
            </p>

            <ACard class="exam-wiz__tag-card">
              <div class="exam-wiz__tag-row">
                <div class="exam-wiz__tag-input-wrap">
                  <i class="material-icons-round">label</i>
                  <input
                    v-model="tagInput"
                    type="text"
                    class="p-input"
                    placeholder="태그 입력 후 Enter 또는 추가 버튼"
                    @keyup.enter="addTag"
                  />
                </div>
                <ABtn kind="ghost" icon="add" :disabled="!tagInput.trim()" @click="addTag">추가</ABtn>
                <ABtn
                  kind="yellow"
                  :icon="tagFetchLoading ? '' : 'search'"
                  :loading="tagFetchLoading"
                  :disabled="tagFetchLoading || examData.examTags.length === 0"
                  @click="fetchQuestionsByTags"
                >{{ tagFetchLoading ? '조회 중' : '조회' }}</ABtn>
              </div>

              <div v-if="examData.examTags.length > 0" class="exam-wiz__tag-chips">
                <span v-for="(tag, idx) in examData.examTags" :key="idx" class="exam-wiz__tag-chip">
                  <i class="material-icons-round">label</i>
                  {{ tag }}
                  <button type="button" class="exam-wiz__tag-x" @click="removeTag(idx)">
                    <i class="material-icons-round">close</i>
                  </button>
                </span>
              </div>
              <div v-else class="exam-wiz__hint exam-wiz__hint--inline">
                <i class="material-icons-round">info</i>
                태그를 1개 이상 추가한 뒤 조회를 누르세요.
              </div>
            </ACard>

            <!-- 결과 -->
            <ACard v-if="tagFetchSummary && tagFetchSummary.total > 0" class="exam-wiz__result-card">
              <div class="exam-wiz__result-banner">
                <i class="material-icons-round">check_circle</i>
                <strong>시험지 구성 완료 — 총 {{ tagFetchSummary.total }}문제</strong>
              </div>

              <div v-if="tagFetchSummary.textbooks.length > 0" class="exam-wiz__result-textbooks">
                <i class="material-icons-round">menu_book</i>
                <span class="exam-wiz__result-label">사용된 교재:</span>
                <APill v-for="tb in tagFetchSummary.textbooks" :key="tb" kind="default">{{ tb }}</APill>
              </div>

              <h6 class="exam-wiz__matrix-h">분포 미리보기 (스킬 × 난이도)</h6>
              <table class="exam-wiz__matrix">
                <thead>
                  <tr>
                    <th>스킬</th>
                    <th class="is-center">하</th>
                    <th class="is-center">중</th>
                    <th class="is-center">상</th>
                    <th class="is-center">합계</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="skill in skills" :key="skill.key">
                    <td><APill :kind="skillPillKind(skill.key)">{{ skill.name }}</APill></td>
                    <td class="is-center" :class="cellClass(distributionMatrix[skill.key].low)">{{ distributionMatrix[skill.key].low }}</td>
                    <td class="is-center" :class="cellClass(distributionMatrix[skill.key].middle)">{{ distributionMatrix[skill.key].middle }}</td>
                    <td class="is-center" :class="cellClass(distributionMatrix[skill.key].high)">{{ distributionMatrix[skill.key].high }}</td>
                    <td class="is-center exam-wiz__matrix-total">{{ distributionMatrix[skill.key].total }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <th>합계</th>
                    <th class="is-center">{{ distributionMatrix.__totals.low }}</th>
                    <th class="is-center">{{ distributionMatrix.__totals.middle }}</th>
                    <th class="is-center">{{ distributionMatrix.__totals.high }}</th>
                    <th class="is-center exam-wiz__matrix-grand">{{ distributionMatrix.__totals.total }}</th>
                  </tr>
                </tfoot>
              </table>
            </ACard>

            <div v-else-if="tagFetchSummary && tagFetchSummary.total === 0" class="exam-wiz__alert exam-wiz__alert--warn">
              <i class="material-icons-round">warning</i>
              매칭되는 문제가 없습니다. 다른 태그를 시도하거나, '직접 구성' 모드로 전환하세요.
            </div>
          </template>

          <!-- B모드 -->
          <template v-else>
            <h5 class="exam-wiz__step-h">문제 분포 설정</h5>
            <p class="exam-wiz__step-d">
              각 스킬별 문제 수와 난이도를 설정하세요.
              <span class="exam-wiz__hint exam-wiz__hint--warn">최대 문제 수: 읽기/문법/어휘/듣기 10개, 말하기/쓰기 5개</span>
            </p>

            <table class="exam-wiz__dist-table">
              <thead>
                <tr>
                  <th>스킬</th>
                  <th>문제 수</th>
                  <th class="is-center">난이도</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="skill in skills" :key="skill.key">
                  <td><APill :kind="skillPillKind(skill.key)">{{ skill.name }}</APill></td>
                  <td>
                    <input
                      v-model.number="examData.questionDistribution[skill.key].count"
                      type="number"
                      class="p-input exam-wiz__count-input"
                      min="0"
                      :max="skill.maxCount"
                      @change="onDistributionChanged"
                      @input="onDistributionChanged"
                    />
                  </td>
                  <td>
                    <div class="exam-wiz__levels">
                      <label
                        v-for="lv in levelOpts"
                        :key="lv.val"
                        class="exam-wiz__level"
                        :class="[
                          { 'is-on': examData.questionDistribution[skill.key].levels.includes(lv.val) },
                          `is-${lv.val}`,
                        ]"
                      >
                        <input
                          type="checkbox"
                          :value="lv.val"
                          :checked="examData.questionDistribution[skill.key].levels.includes(lv.val)"
                          @change="toggleLevel(skill.key, lv.val)"
                        />
                        <span>{{ lv.label }}</span>
                      </label>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <th>합계</th>
                  <th>{{ totalQuestions }}개</th>
                  <th>-</th>
                </tr>
              </tfoot>
            </table>
          </template>
        </div>

        <!-- Step 4: 문제 선택 -->
        <div v-else-if="currentStep === 4" key="step-4" class="exam-wiz__step-body">
          <h5 class="exam-wiz__step-h">{{ examData.creationMode === 'tag' ? '문제 미리보기' : '문제 선택' }}</h5>
          <p class="exam-wiz__step-d">
            <template v-if="examData.creationMode === 'tag'">
              태그로 자동 구성된 문제입니다. 스킬별 탭을 눌러 실제 문제 내용을 확인하세요. (편집 불가)
            </template>
            <template v-else>
              설정한 분포에 따라 문제를 선택하세요.
            </template>
          </p>

          <div class="exam-wiz__selector-grid">
            <div class="exam-wiz__skill-tabs">
              <button
                v-for="skill in skills"
                :key="skill.key"
                type="button"
                class="exam-wiz__skill-tab"
                :class="{ 'is-active': selectedSkillTab === skill.key }"
                @click="selectedSkillTab = skill.key"
              >
                {{ skill.name }}
                <span class="exam-wiz__skill-count">
                  {{ getSelectedQuestionsCount(skill.key) }}/{{ examData.questionDistribution[skill.key].count }}
                </span>
              </button>

              <div class="exam-wiz__score-summary">
                <div
                  v-for="skill in skills.filter(s => examData.questionDistribution[s.key].count > 0)"
                  :key="skill.key"
                  class="exam-wiz__score-row"
                >
                  <span>{{ skill.name }}</span>
                  <span class="exam-wiz__score-val">{{ skillScores[skill.key] }}점</span>
                </div>
                <div class="exam-wiz__score-total">
                  <span>합계</span>
                  <span class="exam-wiz__score-val exam-wiz__score-val--total">{{ totalScore }}점</span>
                </div>
              </div>
            </div>

            <div class="exam-wiz__skill-body">
              <template v-if="examData.creationMode === 'tag'">
                <AEmpty
                  v-if="(examData.selectedQuestions[selectedSkillTab] || []).length === 0"
                  title="자동 선택된 문제가 없습니다"
                  sub="이 스킬에는 매칭된 문제가 없습니다."
                />
                <div v-else class="exam-wiz__ro-list">
                  <div
                    v-for="(q, qIdx) in examData.selectedQuestions[selectedSkillTab]"
                    :key="q.questionId || qIdx"
                    class="exam-wiz__ro-card"
                  >
                    <div class="exam-wiz__ro-head">
                      <APill kind="default">#{{ qIdx + 1 }}</APill>
                      <APill :kind="levelPillKind(q.level)">{{ levelLabel(q.level) }}</APill>
                      <APill kind="yellow">{{ q.score }}점</APill>
                      <span v-if="q.textBook" class="exam-wiz__ro-tb">
                        <i class="material-icons-round">menu_book</i>{{ q.textBook }}
                      </span>
                    </div>
                    <!-- 지시사항 · 예시 · 읽기 지문 — 태그·공백만 있는 빈 HTML 은 그리지 않는다 -->
                    <div v-if="hasHtmlContent(q.instruction)" class="exam-wiz__ro-section">
                      <span class="exam-wiz__ro-section-label"><i class="material-icons-round">info</i>지시사항</span>
                      <!-- eslint-disable-next-line vue/no-v-html -->
                      <div class="exam-wiz__ro-rich" v-html="q.instruction"></div>
                    </div>
                    <div v-if="hasHtmlContent(q.example)" class="exam-wiz__ro-section exam-wiz__ro-section--example">
                      <span class="exam-wiz__ro-section-label"><i class="material-icons-round">lightbulb</i>예시</span>
                      <!-- eslint-disable-next-line vue/no-v-html -->
                      <div class="exam-wiz__ro-rich" v-html="q.example"></div>
                    </div>
                    <div v-if="hasHtmlContent(q.readingScript)" class="exam-wiz__ro-section exam-wiz__ro-section--reading">
                      <span class="exam-wiz__ro-section-label"><i class="material-icons-round">menu_book</i>읽기 지문</span>
                      <!-- eslint-disable-next-line vue/no-v-html -->
                      <div class="exam-wiz__ro-rich" v-html="q.readingScript"></div>
                    </div>
                    <!-- 그룹 오디오 → 문제 오디오. 파일은 Bearer 가 필요한 스트림이라 듣기 버튼으로 받아 blob URL 로 튼다 -->
                    <div
                      v-for="audio in questionAudios(q)"
                      :key="audio.key"
                      class="exam-wiz__ro-section exam-wiz__ro-section--audio"
                    >
                      <span class="exam-wiz__ro-section-label">
                        <i class="material-icons-round">{{ audio.key === 'group' ? 'audio_file' : 'music_note' }}</i>{{ audio.label }}
                      </span>
                      <div v-if="audio.file" class="exam-wiz__ro-audio">
                        <span class="exam-wiz__ro-audio-name">{{ audio.file }}</span>
                        <ABtn
                          v-if="!audioUrls[audio.file]"
                          size="sm"
                          :icon="loadingAudio[audio.file] ? 'hourglass_empty' : 'play_circle'"
                          :loading="!!loadingAudio[audio.file]"
                          @click="loadAudio(audio.file)"
                        >{{ loadingAudio[audio.file] ? '로딩...' : '듣기' }}</ABtn>
                      </div>
                      <div v-if="audio.file && audioUrls[audio.file]" class="exam-wiz__ro-audio-player">
                        <audio controls :src="audioUrls[audio.file]" preload="metadata">브라우저가 오디오 재생을 지원하지 않습니다.</audio>
                      </div>
                      <div v-if="audio.script" class="exam-wiz__ro-script">
                        <span class="exam-wiz__ro-script-label">스크립트</span>
                        <span>{{ audio.script }}</span>
                      </div>
                    </div>
                    <!-- 그룹 동영상 (groupVideos): 주소마다 재생 버튼 → 플레이어(VideoEmbed), 모든 스킬. 검색 응답이 문항마다 복사해 준다(항상 배열).
                         재생은 YouTube 주소만(2026-10-13) — 그 밖의 http(s) 주소는 새 창 링크로만 -->
                    <div v-if="previewVideos(q).length > 0" class="exam-wiz__ro-section exam-wiz__ro-section--video">
                      <span class="exam-wiz__ro-section-label">
                        <i class="material-icons-round">videocam</i>그룹 동영상 {{ previewVideos(q).length }}개
                      </span>
                      <div class="exam-wiz__ro-videos">
                        <div v-for="(video, vi) in previewVideos(q)" :key="vi" class="exam-wiz__ro-video">
                          <div class="exam-wiz__ro-video-row">
                            <a
                              v-if="isValidVideoUrl(video.url) && !isYoutubeVideoUrl(video.url)"
                              :href="video.url"
                              target="_blank"
                              rel="noopener noreferrer"
                              class="exam-wiz__ro-video-url exam-wiz__ro-video-url--link"
                              title="새 창에서 열기"
                            >{{ video.url }}</a>
                            <span v-else class="exam-wiz__ro-video-url">{{ video.url }}</span>
                            <ABtn
                              v-if="isYoutubeVideoUrl(video.url)"
                              size="sm"
                              :icon="isVideoOpen(q, vi) ? 'stop_circle' : 'play_circle'"
                              @click="toggleVideo(q, vi)"
                            >{{ isVideoOpen(q, vi) ? '닫기' : '재생' }}</ABtn>
                          </div>
                          <VideoEmbed
                            v-if="isVideoOpen(q, vi) && isYoutubeVideoUrl(video.url)"
                            :url="video.url"
                            :title="`동영상 ${vi + 1}`"
                            class="exam-wiz__ro-video-embed"
                          />
                        </div>
                      </div>
                    </div>
                    <!-- 문제 글. (내용 없음)은 글·지시사항·예시·지문·오디오·동영상이 모두 비었을 때만 -->
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <div v-if="hasHtmlContent(q.questionText || q.content)" class="exam-wiz__ro-content" v-html="q.questionText || q.content"></div>
                    <div v-else-if="!hasQuestionBody(q)" class="exam-wiz__ro-content"><em>(내용 없음)</em></div>
                    <!-- 선택지: 그림(image) + 글(choiceText) + 정답 표시 -->
                    <ul v-if="(q.choices || []).length > 0" class="exam-wiz__ro-choices">
                      <li
                        v-for="(c, ci) in q.choices"
                        :key="ci"
                        :class="{ 'is-correct': c.isAnswer || c.correct }"
                      >
                        <span class="exam-wiz__ro-num">{{ ci + 1 }})</span>
                        <div class="exam-wiz__ro-choice-content">
                          <img
                            v-if="choiceImageSrc(c)"
                            :src="choiceImageSrc(c)"
                            :alt="`선택지 ${ci + 1} 이미지`"
                            class="exam-wiz__ro-choice-img"
                            @error="handleImageError"
                          />
                          <!-- eslint-disable-next-line vue/no-v-html -->
                          <span v-if="c.choiceText || c.text" v-html="c.choiceText || c.text"></span>
                        </div>
                        <i v-if="c.isAnswer || c.correct" class="material-icons-round exam-wiz__ro-choice-check">check_circle</i>
                      </li>
                    </ul>
                  </div>
                </div>
              </template>

              <QuestionSelector
                v-else
                :skill="selectedSkillTab"
                :required-count="examData.questionDistribution[selectedSkillTab].count"
                :selected-questions="examData.selectedQuestions[selectedSkillTab]"
                :preselected-ids="preselectedIdsBySkill[selectedSkillTab]"
                :allowed-levels="examData.questionDistribution[selectedSkillTab].levels"
                :selected-textbooks="examData.textBooks"
                :textbook-units="textBookUnitsForSearch"
                @update="updateSelectedQuestions"
              />
            </div>
          </div>
        </div>

        <!-- Step 5: 검토 -->
        <div v-else-if="currentStep === 5" key="step-5" class="exam-wiz__step-body">
          <h5 class="exam-wiz__step-h">검토 및 생성</h5>
          <p class="exam-wiz__step-d">최종 정보를 확인하고 시험을 생성하세요.</p>

          <div class="exam-wiz__kpi-grid">
            <ACard>
              <div class="exam-wiz__kpi">
                <div>
                  <p class="exam-wiz__kpi-label">총 문제 수</p>
                  <p class="exam-wiz__kpi-val">{{ totalQuestions }}<span>개</span></p>
                </div>
                <div class="exam-wiz__kpi-icon"><i class="material-icons-round">inventory_2</i></div>
              </div>
            </ACard>
            <ACard>
              <div class="exam-wiz__kpi">
                <div>
                  <p class="exam-wiz__kpi-label">총 점수</p>
                  <p class="exam-wiz__kpi-val">{{ totalScore }}<span>점</span></p>
                </div>
                <div class="exam-wiz__kpi-icon exam-wiz__kpi-icon--ok"><i class="material-icons-round">military_tech</i></div>
              </div>
            </ACard>
            <ACard>
              <div class="exam-wiz__kpi">
                <div>
                  <p class="exam-wiz__kpi-label">시험 시간</p>
                  <p class="exam-wiz__kpi-val">{{ examData.duration }}<span>분</span></p>
                </div>
                <div class="exam-wiz__kpi-icon exam-wiz__kpi-icon--warn"><i class="material-icons-round">schedule</i></div>
              </div>
            </ACard>
            <ACard>
              <div class="exam-wiz__kpi">
                <div>
                  <p class="exam-wiz__kpi-label">상태</p>
                  <p class="exam-wiz__kpi-val">
                    <APill :kind="statusPillKind(examStatusText)">{{ examStatusKorean }}</APill>
                  </p>
                </div>
                <div class="exam-wiz__kpi-icon exam-wiz__kpi-icon--info"><i class="material-icons-round">flag</i></div>
              </div>
            </ACard>
          </div>

          <div class="exam-wiz__summary-grid">
            <ACard>
              <template #title>
                <h6 class="exam-wiz__h6">시험 정보</h6>
              </template>
              <dl class="exam-wiz__dl">
                <dt>시험명</dt>
                <dd>{{ examData.examName }}</dd>
                <dt>시작/종료</dt>
                <dd>{{ formatDateTime(examData.startTime) }} ~ {{ formatDateTime(calcEndTime(examData.startTime, examData.duration)) }}</dd>
                <dt>선택된 교재</dt>
                <dd>
                  <div class="exam-wiz__chips">
                    <span v-for="tb in (examData.textBooks || [])" :key="tb.barcode" class="exam-wiz__chip">
                      {{ tb.title || tb.textBook || tb.barcode }}
                      <small v-if="parseUnitInput(examData.textBookUnits[tb.barcode]).length > 0">
                        ({{ parseUnitInput(examData.textBookUnits[tb.barcode]).join(', ') }}단원)
                      </small>
                    </span>
                    <span v-if="!examData.textBooks || examData.textBooks.length === 0" class="exam-wiz__hint">교재가 선택되지 않았습니다</span>
                  </div>
                </dd>
              </dl>
            </ACard>
            <ACard>
              <template #title>
                <h6 class="exam-wiz__h6">설정 요약</h6>
              </template>
              <div class="exam-wiz__settings-chips">
                <APill v-if="examData.settings.showResults" kind="ok">결과 표시</APill>
                <APill v-if="examData.settings.generateABVersion" kind="warn">A/B 버전</APill>
                <APill :kind="examData.settings.faceToFace ? 'err' : 'default'">
                  {{ examData.settings.faceToFace ? '대면' : '비대면' }}
                </APill>
                <APill kind="info">오디오 {{ examData.settings.audioPlayLimit }}회</APill>
              </div>
            </ACard>
          </div>

          <ACard class="exam-wiz__dist-summary">
            <template #title>
              <h6 class="exam-wiz__h6">문제 분포</h6>
            </template>
            <div class="exam-wiz__dist-grid">
              <div
                v-for="s in filteredSummarySkills"
                :key="s.key"
                class="exam-wiz__dist-item"
              >
                <div class="exam-wiz__dist-head">
                  <APill :kind="skillPillKind(s.key)">{{ s.name }}</APill>
                  <div class="exam-wiz__dist-count">
                    <p>문제 수</p>
                    <strong>{{ examData.questionDistribution[s.key].count }}개</strong>
                  </div>
                </div>
                <div class="exam-wiz__dist-levels">
                  <APill
                    v-for="lv in examData.questionDistribution[s.key].levels"
                    :key="s.key + lv"
                    kind="default"
                  >{{ levelLabel(lv) }}</APill>
                </div>
                <div class="exam-wiz__dist-bar">
                  <div
                    class="exam-wiz__dist-fill"
                    :style="{ width: Math.round((examData.questionDistribution[s.key].count / (totalQuestions || 1)) * 100) + '%' }"
                  ></div>
                </div>
              </div>
            </div>
          </ACard>

          <div v-if="validationErrors.length > 0" class="exam-wiz__alert exam-wiz__alert--err">
            <i class="material-icons-round">error</i>
            <div>
              <h6>다음 항목을 확인해주세요</h6>
              <ul>
                <li v-for="error in validationErrors" :key="error">{{ error }}</li>
              </ul>
            </div>
          </div>
        </div>
      </transition>
    </ACard>

    <!-- Footer Actions -->
    <div class="exam-wiz__foot">
      <ABtn
        kind="ghost"
        icon="arrow_back"
        :disabled="currentStep === 1"
        @click="previousStep"
      >이전</ABtn>
      <ABtn
        v-if="currentStep < 5"
        kind="yellow"
        icon="arrow_forward"
        :disabled="!canProceed"
        @click="nextStep"
      >다음</ABtn>
      <ABtn
        v-else
        kind="primary"
        :icon="isCreating ? '' : 'check'"
        :loading="isCreating"
        :disabled="!isValid || isCreating"
        @click="isEditMode ? updateExamSubmit() : createExam()"
      >{{ isCreating ? (isEditMode ? '수정 중' : '생성 중') : (isEditMode ? '시험 수정' : '시험 생성') }}</ABtn>
    </div>

    <!-- 재조회 확인 모달 -->
    <AModal v-model="needsReloadConfirm" title="교재 또는 단원 범위가 변경되었습니다" size="sm">
      <div class="exam-wiz__reload-confirm">
        <i class="material-icons-round exam-wiz__reload-icon">warning</i>
        <p>
          문제를 재조회하시겠습니까?<br>
          재조회 시 현재 선택된 문제가 <strong>초기화</strong>되며 <strong>복구할 수 없습니다.</strong>
        </p>
      </div>
      <template #foot>
        <ABtn kind="ghost" icon="undo" @click="dismissReload">이전 설정으로 유지</ABtn>
        <ABtn kind="danger-fill" icon="refresh" @click="confirmReload">재조회</ABtn>
      </template>
    </AModal>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import Config from '@/Config';
import { useExam } from '@/composables/useExam';
import { formatKoreanDateTime, formatUtcToTimeZone, COMMON_TIMEZONES } from '@/utils/datetime';
import { hasHtmlContent } from '@/utils/text';
import { normalizeGroupVideos, isValidVideoUrl, isYoutubeVideoUrl } from '@/utils/groupVideos';
import QuestionSelector from './QuestionSelector.vue';
import VideoEmbed from '@/components/VideoEmbed.vue';
import ACard from '@/components/Pineapple/ACard.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';
import APill from '@/components/Pineapple/APill.vue';
import AModal from '@/components/Pineapple/AModal.vue';
import AEmpty from '@/components/Pineapple/AEmpty.vue';

export default {
  name: 'ExamCreateWizard',
  components: { QuestionSelector, VideoEmbed, ACard, ABtn, APill, AModal, AEmpty },
  props: {
    exam: { type: Object, required: false, default: null },
  },
  emits: ['created', 'cancel', 'updated'],
  setup(props, { emit }) {
    const store = useStore();
    const {
      createExam: createExamAction,
      updateExam: updateExamAction,
      validateExamData,
      currentStep,
      wizardSteps,
      nextStep: nextStepAction,
      previousStep: previousStepAction,
      goToStep,
    } = useExam();

    const _instance = getCurrentInstance();
    const _proxy = _instance && _instance.proxy;

    const examData = ref({
      examName: '',
      description: '',
      textBooks: [],
      duration: 60,
      startTime: '',
      endTime: '',
      timeZone: 'Asia/Seoul',
      settings: {
        showResults: true,
        generateABVersion: false,
        audioPlayLimit: 3,
        faceToFace: false,
      },
      questionDistribution: {
        reading: { count: 0, levels: ['middle'] },
        grammar: { count: 0, levels: ['middle'] },
        vocabulary: { count: 0, levels: ['middle'] },
        listening: { count: 0, levels: ['middle'] },
        speaking: { count: 0, levels: ['middle'] },
        writing: { count: 0, levels: ['middle'] },
      },
      selectedQuestions: {
        reading: [], grammar: [], vocabulary: [],
        listening: [], speaking: [], writing: [],
      },
      textBookUnits: {},
      examTags: [],
      creationMode: 'tag',
      status: 'draft',
    });

    const selectedTextbooks = ref([]);
    const selectedSkillTab = ref('reading');
    const validationErrors = ref([]);
    const isCreating = ref(false);
    const needsReloadConfirm = ref(false);
    const lastLoadedSnapshot = ref(null);
    const prevTextbooksSnapshot = ref(null);
    const prevUnitsSnapshot = ref(null);

    const tagInput = ref('');
    const tagFetchLoading = ref(false);
    const tagFetchSummary = ref(null);
    const isEditMode = computed(() => !!(props.exam && (props.exam.examId || props.exam._id)));

    const skills = [
      { key: 'reading',    name: '읽기',   maxCount: 999 },
      { key: 'grammar',    name: '문법',   maxCount: 999 },
      { key: 'vocabulary', name: '어휘',   maxCount: 999 },
      { key: 'listening',  name: '듣기',   maxCount: 999 },
      { key: 'speaking',   name: '말하기', maxCount: 999 },
      { key: 'writing',    name: '쓰기',   maxCount: 999 },
    ];

    const levelOpts = [
      { val: 'low',    label: '하' },
      { val: 'middle', label: '중' },
      { val: 'high',   label: '상' },
    ];

    const getMaxCount = (skillKey) => {
      const skill = skills.find((s) => s.key === skillKey);
      return skill ? skill.maxCount : 10;
    };

    const timeZones = COMMON_TIMEZONES;

    const filteredSummarySkills = computed(() => {
      return skills.filter((s) => (examData.value.questionDistribution[s.key]?.count || 0) > 0);
    });

    const availableTextbooks = ref([]);
    const textbooksLoaded = ref(false);
    const textbookQuery = ref('');

    const fetchTextbooks = async () => {
      try {
        if (!_proxy || !_proxy.$axios) return;
        const res = await _proxy.$axios({
          method: 'get',
          url: '/questions/textBooks',
          api_url: _proxy.$CONF && _proxy.$CONF.ACHIEVE_API_URL,
        });
        const payload = res && res.data;
        const items = Array.isArray(payload) ? payload : ((payload && (payload.items || payload.data)) || []);
        availableTextbooks.value = items.map((tb) => ({
          barcode: tb.barcode || tb.code || tb.id || '',
          title: tb.textBook || tb.title || tb.name || '',
        })).filter((tb) => tb.barcode && tb.title);
        textbooksLoaded.value = true;
      } catch (e) {
        // ignore
      } finally {
        if (!textbooksLoaded.value) textbooksLoaded.value = true;
      }
    };

    onMounted(() => { fetchTextbooks(); });

    const filteredTextbooks = computed(() => {
      const q = (textbookQuery.value || '').toLowerCase().trim();
      if (!q) return availableTextbooks.value;
      return availableTextbooks.value.filter((tb) =>
        (tb.title || '').toLowerCase().includes(q) || (tb.barcode || '').toLowerCase().includes(q)
      );
    });

    const removeTextbook = (code) => {
      selectedTextbooks.value = selectedTextbooks.value.filter((c) => c !== code);
    };

    const parseUnitInput = (input) => {
      if (!input || !input.trim()) return [];
      const units = new Set();
      input.split(',').forEach((part) => {
        part = part.trim();
        if (part.includes('-')) {
          const [start, end] = part.split('-').map(Number);
          if (!isNaN(start) && !isNaN(end)) {
            for (let i = Math.min(start, end); i <= Math.max(start, end); i++) units.add(i);
          }
        } else {
          const n = Number(part);
          if (!isNaN(n) && n > 0) units.add(n);
        }
      });
      return [...units].sort((a, b) => a - b);
    };

    const textBookUnitsForSearch = computed(() => {
      const result = {};
      for (const [barcode, input] of Object.entries(examData.value.textBookUnits)) {
        const units = parseUnitInput(input);
        if (units.length > 0) result[barcode] = units;
      }
      return result;
    });

    const getConditionSnapshot = () => {
      return JSON.stringify({
        textbooks: selectedTextbooks.value.slice().sort(),
        units: examData.value.textBookUnits,
      });
    };

    const saveConditionSnapshot = () => {
      lastLoadedSnapshot.value = getConditionSnapshot();
      prevTextbooksSnapshot.value = JSON.parse(JSON.stringify(selectedTextbooks.value));
      prevUnitsSnapshot.value = JSON.parse(JSON.stringify(examData.value.textBookUnits));
    };

    const hasConditionChanged = () => {
      if (!lastLoadedSnapshot.value) return false;
      return lastLoadedSnapshot.value !== getConditionSnapshot();
    };

    const hasSelectedQuestions = () => {
      return Object.values(examData.value.selectedQuestions).some((arr) => arr && arr.length > 0);
    };

    const confirmReload = () => {
      needsReloadConfirm.value = false;
      Object.keys(examData.value.selectedQuestions).forEach((skill) => {
        examData.value.selectedQuestions[skill] = [];
      });
      saveConditionSnapshot();
      nextStepAction();
    };

    const dismissReload = () => {
      if (prevTextbooksSnapshot.value !== null) {
        selectedTextbooks.value = JSON.parse(JSON.stringify(prevTextbooksSnapshot.value));
      }
      if (prevUnitsSnapshot.value !== null) {
        examData.value.textBookUnits = JSON.parse(JSON.stringify(prevUnitsSnapshot.value));
      }
      needsReloadConfirm.value = false;
    };

    const progressPercentage = computed(() => (currentStep.value / wizardSteps.length) * 100);

    const totalQuestions = computed(() => {
      return Object.values(examData.value.questionDistribution).reduce((sum, dist) => sum + dist.count, 0);
    });

    const totalScore = computed(() => {
      return Object.values(examData.value.selectedQuestions).flat()
        .reduce((sum, q) => sum + (Number(q.score) || 0), 0);
    });

    const skillScores = computed(() => {
      const result = {};
      Object.keys(examData.value.selectedQuestions).forEach((key) => {
        result[key] = (examData.value.selectedQuestions[key] || [])
          .reduce((sum, q) => sum + (Number(q.score) || 0), 0);
      });
      return result;
    });

    const canProceed = computed(() => {
      switch (currentStep.value) {
        case 1: {
          const baseOk = examData.value.examName && examData.value.duration && examData.value.startTime;
          if (examData.value.creationMode === 'tag') return !!baseOk;
          return !!baseOk && selectedTextbooks.value.length > 0;
        }
        case 2: return true;
        case 3:
          if (examData.value.creationMode === 'tag') {
            return !!(tagFetchSummary.value && tagFetchSummary.value.total > 0);
          }
          return totalQuestions.value > 0;
        case 4:
          if (examData.value.creationMode === 'tag') return totalQuestions.value > 0;
          return areAllQuestionsSelected();
        default: return true;
      }
    });

    const isValid = computed(() => {
      const validation = validateExamData(examData.value);
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      validationErrors.value = validation.errors;
      return validation.isValid;
    });

    watch(selectedTextbooks, (newVal) => {
      if (!textbooksLoaded.value && isEditMode.value) return;
      const mapped = availableTextbooks.value.filter((tb) => newVal.includes(tb.barcode));
      if (mapped.length > 0) {
        examData.value.textBooks = mapped;
      } else if (!(isEditMode.value && (examData.value.textBooks || []).length > 0)) {
        examData.value.textBooks = [];
      }

      const newSet = new Set(newVal);
      const updatedUnits = { ...examData.value.textBookUnits };
      let changed = false;
      Object.keys(updatedUnits).forEach((bc) => {
        if (!newSet.has(bc)) {
          delete updatedUnits[bc];
          changed = true;
        }
      });
      if (changed) examData.value.textBookUnits = updatedUnits;
    });

    watch(textbooksLoaded, (loaded) => {
      if (!loaded) return;
      if ((selectedTextbooks.value || []).length > 0) {
        const mapped = availableTextbooks.value.filter((tb) => selectedTextbooks.value.includes(tb.barcode));
        if (mapped.length > 0) examData.value.textBooks = mapped;
      }
    });

    const notify = (type, title, text) => {
      try {
        const n = _proxy && _proxy.$notifyVue;
        if (n) {
          n({ type, title, text });
          return;
        }
      } catch (e) { /* ignore */ }
      try {
        window?.alert && window.alert(text || title || '');
      } catch (e) { /* ignore */ }
    };

    const nextStep = () => {
      if (!canProceed.value) return;
      if (currentStep.value === 1 && hasConditionChanged() && hasSelectedQuestions()) {
        needsReloadConfirm.value = true;
        return;
      }
      if (currentStep.value === 1 && !lastLoadedSnapshot.value) saveConditionSnapshot();
      nextStepAction();
    };

    const previousStep = () => previousStepAction();

    const toggleLevel = (skillKey, levelVal) => {
      const levels = examData.value.questionDistribution[skillKey].levels;
      const idx = levels.indexOf(levelVal);
      if (idx === -1) {
        levels.push(levelVal);
      } else if (levels.length > 1) {
        levels.splice(idx, 1);
      }
      onDistributionChanged();
    };

    const addTag = () => {
      const tag = (tagInput.value || '').trim();
      if (!tag) return;
      if (!examData.value.examTags.includes(tag)) examData.value.examTags.push(tag);
      tagInput.value = '';
    };

    const removeTag = (index) => {
      examData.value.examTags.splice(index, 1);
      tagFetchSummary.value = null;
      Object.keys(examData.value.selectedQuestions).forEach((k) => {
        examData.value.selectedQuestions[k] = [];
        examData.value.questionDistribution[k].count = 0;
        examData.value.questionDistribution[k].levels = ['middle'];
      });
    };

    const emptyBySkill = () => ({
      reading: [], grammar: [], vocabulary: [],
      listening: [], speaking: [], writing: [],
    });

    const fetchQuestionsByTags = async () => {
      if (!examData.value.examTags?.length) {
        notify('warning', '태그 없음', '태그를 1개 이상 입력하세요.');
        return;
      }
      tagFetchLoading.value = true;
      try {
        const barcodes = (examData.value.textBooks || []).map((tb) => tb.barcode).filter(Boolean);
        const req = {
          examTags: examData.value.examTags,
          verified: true,
          ...(barcodes.length ? { textbooks: [...new Set(barcodes)] } : {}),
        };
        const res = await store.dispatch('questionBank/searchQuestions', req);
        const items = res?.items || [];
        if (items.length === 0) {
          tagFetchSummary.value = { total: 0, textbooks: [], fetched: true };
          notify('warning', '조회 결과 없음', '입력한 태그로 매칭되는 문제가 없습니다.');
          return;
        }
        const bySkill = emptyBySkill();
        const usedTextbooks = new Set();
        items.forEach((q) => {
          const skill = (q.skill || '').toLowerCase();
          if (!bySkill[skill]) return;
          bySkill[skill].push(q);
          if (q.textBook) usedTextbooks.add(q.textBook);
        });
        let totalQs = 0;
        skills.forEach((s) => {
          const all = bySkill[s.key] || [];
          const limited = all.slice(0, s.maxCount);
          examData.value.selectedQuestions[s.key] = limited;
          examData.value.questionDistribution[s.key].count = limited.length;
          const levelsSet = new Set(limited.map((q) => q.level).filter(Boolean));
          examData.value.questionDistribution[s.key].levels =
            levelsSet.size > 0 ? [...levelsSet] : ['middle'];
          totalQs += limited.length;
        });
        tagFetchSummary.value = { total: totalQs, textbooks: [...usedTextbooks], fetched: true };
        notify('success', '시험지 구성 완료', `총 ${totalQs}개의 문제가 자동으로 구성되었습니다.`);
      } catch (e) {
        console.error('Tag fetch failed:', e);
        notify('error', '조회 실패', '태그 기반 문제 조회에 실패했습니다.');
        tagFetchSummary.value = null;
      } finally {
        tagFetchLoading.value = false;
      }
    };

    const distributionMatrix = computed(() => {
      const matrix = {};
      const totals = { low: 0, middle: 0, high: 0, total: 0 };
      skills.forEach((s) => {
        const qs = examData.value.selectedQuestions[s.key] || [];
        const row = { low: 0, middle: 0, high: 0, total: qs.length };
        qs.forEach((q) => {
          const lv = (q.level || 'middle').toLowerCase();
          if (row[lv] !== undefined) row[lv]++;
        });
        matrix[s.key] = row;
        totals.low += row.low;
        totals.middle += row.middle;
        totals.high += row.high;
        totals.total += row.total;
      });
      matrix.__totals = totals;
      return matrix;
    });

    const onDistributionChanged = () => {
      Object.keys(examData.value.questionDistribution).forEach((key) => {
        const dist = examData.value.questionDistribution[key];
        const maxCount = getMaxCount(key);
        if (dist.count > maxCount) {
          dist.count = maxCount;
          notify('warning', '문제 수 제한', `${key} 스킬은 최대 ${maxCount}개까지 설정할 수 있습니다.`);
        }
        const selected = examData.value.selectedQuestions[key] || [];
        if (selected.length > dist.count) {
          examData.value.selectedQuestions[key] = selected.slice(0, dist.count);
        }
      });
    };

    const getSelectedQuestionsCount = (skill) => examData.value.selectedQuestions[skill]?.length || 0;

    const updateSelectedQuestions = ({ skill, questions }) => {
      examData.value.selectedQuestions[skill] = questions;
    };

    const areAllQuestionsSelected = () => {
      return Object.keys(examData.value.questionDistribution).every((skill) => {
        const required = examData.value.questionDistribution[skill].count;
        const selected = examData.value.selectedQuestions[skill]?.length || 0;
        return selected >= required;
      });
    };

    const preloadSelections = async () => {
      const skillsKeys = Object.keys(examData.value.questionDistribution);
      for (const key of skillsKeys) {
        const needed = examData.value.questionDistribution[key].count;
        if (!needed) continue;
        const currentQuestions = examData.value.selectedQuestions[key] || [];
        const preIds = preselectedIdsBySkill.value[key] || [];
        const hasCompleteData = currentQuestions.length > 0 &&
          currentQuestions.every((q) => q.questionNumber && q.type && q.textBook);
        if (hasCompleteData && currentQuestions.length >= needed) continue;
        if (preIds.length === 0) continue;

        const req = { skill: key, verified: true };
        const lvls = (examData.value.questionDistribution[key].levels || []).map((l) => (l || '').toLowerCase());
        if (lvls.length > 0) req.levels = lvls;
        if (examData.value.textBooks && examData.value.textBooks.length > 0) {
          const barcodes = examData.value.textBooks.map((tb) => tb.barcode).filter(Boolean);
          if (barcodes.length > 0) req.textbooks = [...new Set(barcodes)];
        }
        const unitsMap = textBookUnitsForSearch.value;
        if (unitsMap && Object.keys(unitsMap).length > 0) req.textbookUnits = unitsMap;

        try {
          await store.dispatch('questionBank/searchQuestions', req);
          const results = store.getters['questionBank/getSearchResults'] || [];
          const matchSet = new Set(preIds);
          const matched = results.filter((q) => matchSet.has(q.questionId));
          if (matched.length > 0) {
            const savedScoreMap = new Map();
            const savedQuestions = props.exam?.versions?.A?.questions || [];
            savedQuestions.forEach((q) => {
              if (q.questionId) savedScoreMap.set(q.questionId, q.score ?? null);
            });
            const matchMap = new Map(matched.map((q) => [q.questionId, q]));
            const merged = preIds
              .map((id) => {
                const q = matchMap.get(id);
                if (!q) return null;
                const savedScore = savedScoreMap.get(id);
                return (savedScore !== null && savedScore !== undefined) ? { ...q, score: savedScore } : q;
              })
              .filter(Boolean)
              .slice(0, needed);
            examData.value.selectedQuestions[key] = merged;
          }
        } catch (e) { /* ignore per-skill errors */ }
      }
    };

    const preselectedIdsBySkill = computed(() => {
      if (!isEditMode.value || !props.exam) {
        return { reading: [], grammar: [], vocabulary: [], listening: [], speaking: [], writing: [] };
      }
      const obj = { reading: [], grammar: [], vocabulary: [], listening: [], speaking: [], writing: [] };
      const versionA = props.exam?.versions?.A;
      if (versionA && Array.isArray(versionA.questions) && versionA.questions.length > 0) {
        versionA.questions.forEach((q) => {
          const skill = (q.skill || '').toLowerCase();
          const qId = q.questionId || q.id || q._id;
          if (obj[skill] && qId) obj[skill].push(qId);
        });
        if (Object.values(obj).some((arr) => arr.length > 0)) return obj;
      }
      if (props.exam.selectedQuestions && typeof props.exam.selectedQuestions === 'object') {
        Object.keys(obj).forEach((skill) => {
          const questions = props.exam.selectedQuestions[skill];
          if (Array.isArray(questions)) {
            obj[skill] = questions.map((q) => {
              if (typeof q === 'string') return q;
              return q.questionId || q.id || q._id;
            }).filter(Boolean);
          }
        });
        if (Object.values(obj).some((arr) => arr.length > 0)) return obj;
      }
      const versionIds = (props.exam?.versions && props.exam.versions.A && Array.isArray(props.exam.versions.A.questionIds))
        ? props.exam.versions.A.questionIds
        : [];
      if (versionIds.length > 0) Object.keys(obj).forEach((k) => { obj[k] = versionIds; });
      return obj;
    });

    const hydrateFromExam = (ex) => {
      if (!ex) return;
      examData.value.examName = ex.examName || '';
      examData.value.description = ex.description || '';
      examData.value.duration = ex.duration || 60;
      const tz = ex.timeZone || (ex.settings && ex.settings.timeZone) || 'Asia/Seoul';
      const startLocal = ex.startTimeUTC ? formatUtcToTimeZone(ex.startTimeUTC, tz) : '';
      const endLocal = ex.endTimeUTC ? formatUtcToTimeZone(ex.endTimeUTC, tz) : '';
      examData.value.startTime = startLocal ? startLocal.slice(0, 16) : '';
      examData.value.endTime = endLocal ? endLocal.slice(0, 16) : '';
      examData.value.status = ex.status || examData.value.status;
      examData.value.settings = {
        showResults: !!(ex.settings && ex.settings.showResults),
        generateABVersion: !!(ex.settings && ex.settings.generateABVersion),
        audioPlayLimit: (ex.settings && ex.settings.audioPlayLimit) || 3,
        faceToFace: !!(ex.settings && ex.settings.faceToFace),
      };
      examData.value.timeZone = ex.timeZone || (ex.settings && ex.settings.timeZone) || 'Asia/Seoul';
      examData.value.textBooks = Array.isArray(ex.textBooks) ? JSON.parse(JSON.stringify(ex.textBooks)) : [];
      examData.value.textBookUnits = ex.textBookUnits ? JSON.parse(JSON.stringify(ex.textBookUnits)) : {};
      examData.value.examTags = Array.isArray(ex.examTags) ? [...ex.examTags] : [];
      examData.value.creationMode = (ex.creationMode === 'tag' || examData.value.examTags.length > 0) ? 'tag' : 'manual';
      selectedTextbooks.value = (examData.value.textBooks || []).map((tb) => tb.barcode).filter(Boolean);
      const def = {
        reading: { count: 0, levels: ['middle'] },
        grammar: { count: 0, levels: ['middle'] },
        vocabulary: { count: 0, levels: ['middle'] },
        listening: { count: 0, levels: ['middle'] },
        speaking: { count: 0, levels: ['middle'] },
        writing: { count: 0, levels: ['middle'] },
      };
      const dist = ex.questionDistribution || {};
      examData.value.questionDistribution = {
        reading: { ...def.reading, ...(dist.reading || {}) },
        grammar: { ...def.grammar, ...(dist.grammar || {}) },
        vocabulary: { ...def.vocabulary, ...(dist.vocabulary || {}) },
        listening: { ...def.listening, ...(dist.listening || {}) },
        speaking: { ...def.speaking, ...(dist.speaking || {}) },
        writing: { ...def.writing, ...(dist.writing || {}) },
      };

      const versionA = ex.versions && ex.versions.A;
      const versionQuestions = versionA && Array.isArray(versionA.questions) ? versionA.questions : [];

      examData.value.selectedQuestions = {
        reading: [], grammar: [], vocabulary: [],
        listening: [], speaking: [], writing: [],
      };

      if (versionQuestions.length > 0) {
        versionQuestions.forEach((q) => {
          const skill = (q.skill || '').toLowerCase();
          if (examData.value.selectedQuestions[skill]) {
            examData.value.selectedQuestions[skill].push({
              questionId: q.questionId || q.id || q._id,
              questionNumber: q.questionNumber || q.number || '',
              level: (q.level || 'middle').toLowerCase(),
              content: q.content || q.questionText || '',
              type: q.type || 'MULTIPLE_CHOICE',
              textBook: q.textBook || q.textbook || '',
              unit: q.unit || '',
              timeLimit: q.timeLimit || q.time_limit || 60,
              score: q.score || 1,
              audioFile: q.audioFile || q.audio_file || q.questionAudio || null,
              choices: q.choices || [],
              instruction: q.instruction || '',
              example: q.example || '',
              readingScript: q.readingScript || '',
              ...q,
            });
          }
        });

        const selectedBySkill = ex.selectedQuestions || null;
        if (selectedBySkill && typeof selectedBySkill === 'object') {
          Object.keys(examData.value.selectedQuestions).forEach((skill) => {
            const idArray = selectedBySkill[skill];
            if (Array.isArray(idArray) && idArray.length > 0) {
              const currentCount = examData.value.selectedQuestions[skill].length;
              const neededCount = examData.value.questionDistribution[skill]?.count || 0;
              if (currentCount < neededCount) {
                const idSet = new Set(idArray);
                const questionsToAdd = versionQuestions
                  .filter((q) => idSet.has(q.questionId))
                  .filter((q) => !examData.value.selectedQuestions[skill].some((existing) => existing.questionId === q.questionId))
                  .slice(0, neededCount - currentCount);
                questionsToAdd.forEach((q) => {
                  examData.value.selectedQuestions[skill].push({
                    questionId: q.questionId || q.id || q._id,
                    questionNumber: q.questionNumber || q.number || '',
                    level: (q.level || 'middle').toLowerCase(),
                    content: q.content || q.questionText || '',
                    type: q.type || 'MULTIPLE_CHOICE',
                    textBook: q.textBook || q.textbook || '',
                    unit: q.unit || '',
                    timeLimit: q.timeLimit || q.time_limit || 60,
                    score: q.score || 1,
                    audioFile: q.audioFile || q.audio_file || q.questionAudio || null,
                    choices: q.choices || [],
                    instruction: q.instruction || '',
                    example: q.example || '',
                    readingScript: q.readingScript || '',
                    ...q,
                  });
                });
              }
            }
          });
        }
      } else {
        const selectedBySkill = ex.selectedQuestions || null;
        if (selectedBySkill && typeof selectedBySkill === 'object') {
          Object.keys(examData.value.selectedQuestions).forEach((k) => {
            const arr = selectedBySkill[k];
            if (Array.isArray(arr) && arr.length > 0) {
              const firstItem = arr[0];
              if (typeof firstItem === 'object' && firstItem !== null && 'questionText' in firstItem) {
                examData.value.selectedQuestions[k] = arr.map((q) => ({
                  questionId: q.questionId || q.id || q._id,
                  questionNumber: q.questionNumber || q.number || '',
                  level: (q.level || 'middle').toLowerCase(),
                  content: q.content || q.questionText || '',
                  type: q.type || 'MULTIPLE_CHOICE',
                  textBook: q.textBook || q.textbook || '',
                  unit: q.unit || '',
                  timeLimit: q.timeLimit || q.time_limit || 60,
                  score: q.score || 1,
                  audioFile: q.audioFile || q.audio_file || null,
                  choices: q.choices || [],
                  ...q,
                }));
              }
            }
          });
        }
        const versionIds = versionA && Array.isArray(versionA.questionIds) ? versionA.questionIds : [];
        if (versionIds.length > 0 && Object.values(examData.value.selectedQuestions).every((arr) => arr.length === 0)) {
          examData.value.selectedQuestions.reading = versionIds.map((id) => ({ questionId: id }));
        }
      }
    };

    onMounted(async () => {
      if (isEditMode.value) {
        hydrateFromExam(props.exam);
        await preloadSelections();
        setTimeout(() => { saveConditionSnapshot(); }, 500);
      }
    });

    watch(() => props.exam, (nv) => { if (nv) hydrateFromExam(nv); });

    const createExam = async () => {
      if (!isValid.value) return;
      isCreating.value = true;
      try {
        const result = await createExamAction(examData.value);
        notify('success', '시험 생성', '시험이 성공적으로 등록되었습니다.');
        emit('created', result);
      } catch (error) {
        console.error('Failed to create exam:', error);
        alert('시험 생성에 실패했습니다.');
      } finally {
        isCreating.value = false;
      }
    };

    const updateExamSubmit = async () => {
      if (!isValid.value || !isEditMode.value) return;
      isCreating.value = true;
      try {
        const id = props.exam.examId || props.exam._id;
        const result = await updateExamAction(id, examData.value);
        notify('success', '시험 수정', '시험이 성공적으로 수정되었습니다.');
        emit('updated', result);
      } catch (error) {
        console.error('Failed to update exam:', error);
        alert('시험 수정에 실패했습니다.');
      } finally {
        isCreating.value = false;
      }
    };

    // ── 태그 모드 문제 미리보기(4단계) — 생성 뒤 미리보기(ExamDetailModal)·문제 선택 상세와 같은 구성 ──
    // 듣기 문항은 questionText 가 비고 본문이 questionAudio(파일·스크립트)·instruction·그림 선택지에 있다.
    // 글과 글 선택지만 그리면 전부 빈 칸이 된다(2026-10-08, 강사 화면팀 전달과 같은 수정).
    const audioUrls = reactive({});
    const loadingAudio = reactive({});

    const loadAudio = async (fileName) => {
      if (!fileName || audioUrls[fileName]) return;
      loadingAudio[fileName] = true;
      try {
        const blobUrl = await _proxy.$getAudioStreamUrl(fileName, 'exam_files', Config.ACHIEVE_API_URL);
        if (blobUrl) audioUrls[fileName] = blobUrl;
      } catch (error) {
        console.error('오디오 로드 실패:', error);
      } finally {
        loadingAudio[fileName] = false;
      }
    };

    onBeforeUnmount(() => {
      Object.values(audioUrls).forEach((url) => {
        try { URL.revokeObjectURL(url); } catch (e) { /* ignore */ }
      });
    });

    // 그룹 오디오 → 문제 오디오 순. file 이나 script 가 있는 것만 그린다.
    const questionAudios = (q) => {
      const list = [];
      const push = (key, label, audio) => {
        if (audio && (audio.file || audio.script)) list.push({ key, label, file: audio.file || '', script: audio.script || '' });
      };
      push('group', '그룹 오디오', q.groupAudio);
      push('question', '문제 오디오', q.questionAudio || q.audioFile);
      return list;
    };

    // 그룹 동영상 URL 목록 — 검색 응답이 문항마다 복사(2026-10-12, 항상 배열). 예전 값·null 도 [] 로
    const previewVideos = (q) => normalizeGroupVideos(q.groupVideos);

    // 그룹 동영상 플레이어 — 같은 그룹 값이 문항마다 되풀이되므로 재생 버튼을 누른 것만 그린다 (키: 문항 ID + 순번)
    const openVideos = reactive({});
    const videoKey = (q, index) => `${q.questionId || ''}:${index}`;
    const toggleVideo = (q, index) => { openVideos[videoKey(q, index)] = !openVideos[videoKey(q, index)]; };
    const isVideoOpen = (q, index) => !!openVideos[videoKey(q, index)];

    // 글·지시사항·예시·읽기 지문·오디오·동영상 가운데 하나라도 있으면 본문이 있다. 모두 비었을 때만 (내용 없음)
    const hasQuestionBody = (q) =>
      hasHtmlContent(q.questionText || q.content) || hasHtmlContent(q.instruction) ||
      hasHtmlContent(q.example) || hasHtmlContent(q.readingScript) || questionAudios(q).length > 0 ||
      previewVideos(q).length > 0;

    // 선택지 그림: image.url 이 절대 URL 이면 그대로, 아니면 image.file 을 이미지 스트림(인증 없음)으로 받는다.
    const choiceImageSrc = (choice) => {
      const image = choice && choice.image;
      if (!image) return '';
      if (/^https?:\/\//i.test(image.url || '')) return image.url;
      return image.file ? `${Config.ACHIEVE_API_URL}/files/image/stream/${image.file}?file_type=exam_files` : '';
    };

    const handleImageError = (event) => {
      console.error('이미지 로드 실패:', event.target.src);
      event.target.style.display = 'none';
    };

    const skillPillKind = (skill) => {
      const map = {
        reading: 'info', grammar: 'ok', vocabulary: 'yellow',
        listening: 'warn', speaking: 'err', writing: 'default',
      };
      return map[skill] || 'default';
    };

    const levelPillKind = (level) => {
      const l = (level || '').toLowerCase();
      if (l === 'low') return 'ok';
      if (l === 'middle') return 'warn';
      if (l === 'high') return 'err';
      return 'default';
    };

    const levelLabel = (l) => {
      const v = (l || '').toLowerCase();
      if (v === 'low') return '하';
      if (v === 'middle') return '중';
      if (v === 'high') return '상';
      return l;
    };

    const statusPillKind = (status) => {
      const map = { draft: 'default', scheduled: 'warn', active: 'ok', completed: 'info' };
      return map[String(status || '').toLowerCase()] || 'default';
    };

    const cellClass = (value) => {
      if (value === 0) return 'exam-wiz__matrix-zero';
      return 'exam-wiz__matrix-has';
    };

    const examStatusText = computed(() => examData.value.status || 'draft');
    const examStatusKorean = computed(() => {
      const map = { draft: '초안', scheduled: '예정', active: '진행중', completed: '완료' };
      const s = String(examStatusText.value || '').toLowerCase();
      return map[s] || s;
    });

    const formatDateTime = (datetime) => formatKoreanDateTime(datetime);

    const calcEndTime = (startTime, durationMinutes) => {
      if (!startTime || !durationMinutes) return '';
      const start = new Date(startTime);
      const end = new Date(start.getTime() + Number(durationMinutes) * 60000);
      return end.toISOString();
    };

    const getStepDescription = (stepId) => {
      const descriptions = {
        1: '시험 기본 정보 입력',
        2: '시험 옵션 설정',
        3: '문제 분포 구성',
        4: '문제 선택',
        5: '최종 검토 및 생성',
      };
      return descriptions[stepId] || '';
    };

    return {
      examData, selectedTextbooks, selectedSkillTab, validationErrors, isCreating,
      skills, levelOpts, availableTextbooks, currentStep, wizardSteps, progressPercentage,
      totalQuestions, totalScore, skillScores, canProceed, isValid,
      nextStep, previousStep, goToStep, onDistributionChanged, toggleLevel,
      getSelectedQuestionsCount, updateSelectedQuestions, createExam, updateExamSubmit,
      skillPillKind, levelPillKind, levelLabel, statusPillKind, cellClass,
      examStatusText, examStatusKorean, formatDateTime, calcEndTime, getStepDescription,
      textbookQuery, filteredTextbooks, removeTextbook, parseUnitInput, textBookUnitsForSearch,
      needsReloadConfirm, confirmReload, dismissReload, filteredSummarySkills,
      isEditMode, preselectedIdsBySkill, timeZones, getMaxCount,
      tagInput, tagFetchLoading, tagFetchSummary,
      addTag, removeTag, fetchQuestionsByTags, distributionMatrix,
      hasHtmlContent, audioUrls, loadingAudio, loadAudio, questionAudios, hasQuestionBody, choiceImageSrc, handleImageError,
      previewVideos, toggleVideo, isVideoOpen, isValidVideoUrl, isYoutubeVideoUrl,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.exam-wiz {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__head {
    padding: 18px 0;
  }

  &__title {
    margin: 0;
    font-size: 24px;
    font-weight: 900;
    color: $p-ink;
    letter-spacing: -0.02em;
    display: inline-flex;
    align-items: center;
    gap: 10px;

    > i {
      font-size: 28px !important;
      color: $p-point-deep;
    }
  }

  &__sub {
    margin: 4px 0 0;
    font-size: 12px;
    color: $p-ink-soft;
  }

  // ── Steps ──
  &__steps {
    position: relative;
    padding: 0 20px;
  }

  &__steps-track {
    position: absolute;
    top: 24px;
    left: 60px;
    right: 60px;
    height: 3px;
    background: $p-line-soft;
    border-radius: 2px;
    overflow: hidden;
    z-index: 0;
  }

  &__steps-progress {
    height: 100%;
    background: $p-point-deep;
    transition: width $p-dur $p-ease;
  }

  &__steps-list {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 8px;
    position: relative;
    z-index: 1;
  }

  &__step {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    cursor: pointer;
    padding: 4px;
    border-radius: $p-radius-md;
    transition: background $p-dur $p-ease;

    &:hover:not(.is-disabled) {
      background: $p-panel-alt;
    }

    &.is-disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }
  }

  &__step-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: $p-panel;
    border: 2px solid $p-line;
    color: $p-ink-soft;
    display: grid;
    place-items: center;
    transition: all $p-dur $p-ease;
    margin-bottom: 8px;

    > i { font-size: 22px !important; }

    .exam-wiz__step.is-active & {
      background: $p-point-deep;
      border-color: $p-point-deep;
      color: $p-panel;
      transform: scale(1.05);
    }

    .exam-wiz__step.is-done & {
      background: $p-ok;
      border-color: $p-ok;
      color: $p-panel;
    }
  }

  &__step-num {
    font-size: 9px;
    font-weight: 800;
    color: $p-ink-soft;
    letter-spacing: 0.1em;
    margin-bottom: 2px;
  }

  &__step-title {
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
  }

  &__step-desc {
    font-size: 10px;
    color: $p-ink-soft;
    margin-top: 2px;
  }

  // ── Body ──
  &__body {
    min-height: 400px;
  }

  &__step-body {
    padding: 4px;
  }

  &__step-h {
    margin: 0 0 8px;
    font-size: 16px;
    font-weight: 800;
    color: $p-ink;
  }

  &__step-d {
    margin: 0 0 18px;
    font-size: 12px;
    color: $p-ink-mid;
    line-height: 1.6;
  }

  &__field-section {
    margin-top: 18px;
  }

  &__hint {
    font-size: 11px;
    color: $p-ink-soft;
    font-weight: 500;

    &--warn { color: $p-warn; font-weight: 700; }
    &--inline {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      margin-top: 10px;

      > i { font-size: 14px !important; }
    }
  }

  &__hint-inline {
    font-size: 10px;
    color: $p-ink-soft;
    font-weight: 500;
    margin-left: 4px;
  }

  // ── 모드 카드 ──
  &__mode {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;

    @media (max-width: 768px) { grid-template-columns: 1fr; }
  }

  &__mode-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    background: $p-panel;
    border: 2px solid $p-line;
    border-radius: $p-radius-md;
    cursor: pointer;
    transition: border-color $p-dur $p-ease, background $p-dur $p-ease;

    > input { display: none; }

    &:hover {
      border-color: $p-point-deep;
    }

    &.is-active {
      border-color: $p-point-deep;
      background: $p-point-soft;
    }

    strong { display: block; font-size: 13px; font-weight: 800; color: $p-ink; margin-bottom: 2px; }
    p { margin: 0; font-size: 11px; color: $p-ink-mid; }
  }

  &__mode-icon {
    font-size: 28px !important;
    color: $p-point-deep;
  }

  // ── 교재 선택 ──
  &__textbook-select {
    margin-top: 8px;
    min-height: 140px;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px 4px 10px;
    background: $p-point-soft;
    color: $p-point-deep;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;

    small { font-weight: 500; opacity: 0.8; }
  }

  &__chip-x {
    width: 16px;
    height: 16px;
    border: 0;
    background: transparent;
    color: $p-point-deep;
    border-radius: 50%;
    display: grid;
    place-items: center;
    cursor: pointer;

    > i { font-size: 12px !important; }

    &:hover { background: rgba(201, 138, 10, 0.2); }
  }

  &__empty-hint {
    padding: 14px 16px;
    background: $p-panel-alt;
    border: 1px dashed $p-line;
    border-radius: $p-radius-md;
    color: $p-ink-soft;
    font-size: 12px;
    text-align: center;
  }

  &__unit-row {
    margin-bottom: 10px;
    padding: 10px 12px;
    background: $p-panel-alt;
    border-radius: $p-radius-sm;

    strong {
      display: block;
      font-size: 12px;
      color: $p-ink;
      margin-bottom: 4px;
    }
  }

  // ── 태그 모드 ──
  &__tag-card {
    margin-bottom: 14px;
  }

  &__tag-row {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  &__tag-input-wrap {
    flex: 1;
    position: relative;

    > i {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 18px !important;
      color: $p-ink-soft;
      pointer-events: none;
    }

    > .p-input { padding-left: 38px; }
  }

  &__tag-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 12px;
  }

  &__tag-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 4px 4px 10px;
    background: $p-point-soft;
    color: $p-point-deep;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;

    > i { font-size: 14px !important; }
  }

  &__tag-x {
    width: 18px;
    height: 18px;
    border: 0;
    background: rgba(201, 138, 10, 0.15);
    color: $p-point-deep;
    border-radius: 50%;
    display: grid;
    place-items: center;
    cursor: pointer;

    > i { font-size: 12px !important; }

    &:hover { background: rgba(201, 138, 10, 0.3); }
  }

  // ── 결과 ──
  &__result-card {
    margin-top: 12px;
  }

  &__result-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: $p-ok-soft;
    border-left: 3px solid $p-ok;
    border-radius: $p-radius-sm;
    color: $p-ok;
    font-size: 13px;
    margin-bottom: 14px;

    > i { font-size: 18px; }
  }

  &__result-textbooks {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 12px;
    color: $p-ink-mid;
    margin-bottom: 14px;

    > i { font-size: 16px !important; color: $p-info; }
  }

  &__result-label {
    font-weight: 800;
    margin-right: 4px;
  }

  &__matrix-h {
    margin: 0 0 8px;
    font-size: 12px;
    font-weight: 800;
    color: $p-ink2;
  }

  &__matrix {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;

    th, td {
      padding: 8px 10px;
      border: 1px solid $p-line;
      text-align: left;
    }

    th {
      background: $p-panel-alt;
      color: $p-ink-mid;
      font-weight: 800;
    }

    .is-center { text-align: center; }
  }

  &__matrix-zero { color: $p-ink-softer; }
  &__matrix-has  { color: $p-ink; font-weight: 700; }
  &__matrix-total { font-weight: 900; color: $p-point-deep; background: $p-point-soft; }
  &__matrix-grand { background: $p-ink; color: $p-point; font-weight: 900; }

  // ── 분포 테이블 ──
  &__dist-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;

    th, td {
      padding: 10px 12px;
      border-bottom: 1px solid $p-line;
      text-align: left;
      vertical-align: middle;
    }

    thead th {
      background: $p-bg;
      color: $p-ink-soft;
      font-size: 10px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      font-weight: 800;
    }

    .is-center { text-align: center; }

    tfoot th {
      background: $p-panel-alt;
      font-weight: 900;
      color: $p-ink;
    }
  }

  &__count-input {
    width: 80px;
    height: 32px;
    padding: 0 8px;
    text-align: center;
    font-family: $p-font-mono;
    font-weight: 700;
  }

  &__levels {
    display: inline-flex;
    gap: 6px;
  }

  &__level {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    background: $p-panel-alt;
    border: 1px solid $p-line;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 700;
    color: $p-ink-mid;
    cursor: pointer;
    transition: all $p-dur $p-ease;

    > input { display: none; }

    &:hover { background: $p-point-soft; }

    &.is-on {
      background: $p-ink;
      color: $p-point;
      border-color: $p-ink;
    }
  }

  // ── 알림 ──
  &__alert {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 12px 14px;
    border-radius: $p-radius-md;
    margin-top: 12px;

    > i { font-size: 18px; flex-shrink: 0; }

    h6 { margin: 0 0 4px; font-size: 13px; font-weight: 800; }
    ul { margin: 0; padding-left: 18px; font-size: 12px; }

    &--warn {
      background: $p-warn-soft;
      border-left: 3px solid $p-warn;
      color: $p-warn;
    }

    &--err {
      background: $p-err-soft;
      border-left: 3px solid $p-err;
      color: $p-err;
    }
  }

  // ── 문제 선택 ──
  &__selector-grid {
    display: grid;
    grid-template-columns: 220px 1fr;
    gap: 18px;

    @media (max-width: 992px) {
      grid-template-columns: 1fr;
    }
  }

  &__skill-tabs {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__skill-tab {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    color: $p-ink-mid;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: all $p-dur $p-ease;

    &:hover {
      background: $p-point-soft;
      color: $p-point-deep;
    }

    &.is-active {
      background: $p-ink;
      color: $p-point;
      border-color: $p-ink;
    }
  }

  &__skill-count {
    padding: 2px 8px;
    background: rgba(255, 255, 255, 0.15);
    color: inherit;
    border-radius: 999px;
    font-family: $p-font-mono;
    font-size: 10px;
    font-weight: 800;

    .exam-wiz__skill-tab:not(.is-active) & {
      background: $p-point-soft;
      color: $p-point-deep;
    }
  }

  &__score-summary {
    margin-top: 12px;
    padding: 12px;
    background: $p-panel-alt;
    border-radius: $p-radius-md;
    font-size: 12px;
  }

  &__score-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
    color: $p-ink-mid;
  }

  &__score-val {
    font-family: $p-font-mono;
    font-weight: 700;
    color: $p-ink;

    &--total {
      font-size: 14px;
      font-weight: 900;
      color: $p-point-deep;
    }
  }

  &__score-total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0 0;
    margin-top: 6px;
    border-top: 1px solid $p-line;
    font-weight: 800;
    color: $p-ink;
  }

  // ── Readonly 미리보기 ──
  &__ro-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__ro-card {
    padding: 14px 16px;
    background: $p-panel;
    border: 1px solid $p-line;
    border-left: 3px solid $p-point-deep;
    border-radius: $p-radius-md;
  }

  &__ro-head {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 8px;
  }

  &__ro-tb {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: $p-ink-soft;

    > i { font-size: 14px !important; }
  }

  &__ro-content {
    font-size: 13px;
    color: $p-ink;
    line-height: 1.6;
    margin-bottom: 8px;

    :deep(p) { margin: 0 0 4px; }
  }

  &__ro-choices {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 4px;

    li {
      display: flex;
      align-items: flex-start;
      gap: 6px;
      padding: 6px 10px;
      background: $p-panel-alt;
      border-radius: $p-radius-sm;
      font-size: 12px;
      color: $p-ink2;

      &.is-correct {
        background: $p-ok-soft;
        color: $p-ok;
        font-weight: 700;
      }
    }
  }

  &__ro-num {
    font-weight: 800;
    color: $p-ink-mid;
  }

  &__ro-section {
    margin-bottom: 8px;
    padding: 8px 10px;
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-left: 3px solid $p-point-deep;
    border-radius: $p-radius-sm;

    &--example { border-left-color: $p-warn; }
    &--reading { border-left-color: $p-info; }
    &--audio { border-left-color: $p-ok; }
    &--video { border-left-color: $p-ink2; }
  }

  &__ro-section-label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 4px;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: $p-ink-soft;

    > i { font-size: 13px !important; }
  }

  &__ro-rich {
    font-size: 12px;
    color: $p-ink2;
    line-height: 1.6;

    :deep(p) { margin: 0 0 4px; &:last-child { margin-bottom: 0; } }
    :deep(img) {
      max-width: 100%;
      max-height: 240px;
      object-fit: contain;
      display: block;
      margin: 6px 0;
      border-radius: $p-radius-sm;
    }
  }

  &__ro-audio {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__ro-audio-name {
    flex: 1;
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-ink2;
    word-break: break-all;
  }

  &__ro-audio-player {
    margin-top: 6px;

    audio { width: 100%; height: 32px; }
  }

  &__ro-script {
    display: flex;
    gap: 6px;
    margin-top: 6px;
    font-size: 11px;
    color: $p-ink2;
    line-height: 1.5;
    white-space: pre-wrap;
  }

  &__ro-script-label {
    flex-shrink: 0;
    align-self: center;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: $p-point-deep;
  }

  &__ro-videos {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__ro-video {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__ro-video-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__ro-video-url {
    flex: 1;
    min-width: 0;
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-ink2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    // YouTube 가 아닌 http(s) 주소 — 플레이어 없이 새 창 링크
    &--link {
      color: $p-info;
      text-decoration: underline;
    }
  }

  &__ro-video-embed {
    margin-top: 2px;
  }

  &__ro-choice-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__ro-choice-img {
    max-width: 100%;
    max-height: 200px;
    object-fit: contain;
    border-radius: $p-radius-sm;
    border: 1px solid $p-line;
    background: $p-panel;
  }

  &__ro-choice-check {
    margin-left: auto;
    color: $p-ok;
    font-size: 16px !important;
  }

  // ── KPI ──
  &__kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 14px;

    @media (max-width: 992px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 576px) { grid-template-columns: 1fr; }
  }

  &__kpi {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  &__kpi-label {
    margin: 0;
    font-size: 10px;
    font-weight: 800;
    color: $p-ink-soft;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  &__kpi-val {
    margin: 6px 0 0;
    font-family: $p-font-mono;
    font-size: 22px;
    font-weight: 900;
    color: $p-ink;

    span {
      font-size: 12px;
      font-weight: 700;
      color: $p-ink-mid;
      margin-left: 4px;
    }
  }

  &__kpi-icon {
    width: 44px;
    height: 44px;
    border-radius: $p-radius-md;
    background: $p-point-soft;
    color: $p-point-deep;
    display: grid;
    place-items: center;
    flex-shrink: 0;

    > i { font-size: 22px; }

    &--ok   { background: $p-ok-soft;   color: $p-ok; }
    &--warn { background: $p-warn-soft; color: $p-warn; }
    &--info { background: $p-info-soft; color: $p-info; }
  }

  // ── 요약 ──
  &__summary-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 12px;

    @media (max-width: 992px) { grid-template-columns: 1fr; }
  }

  &__h6 {
    margin: 0;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
  }

  &__dl {
    margin: 0;
    display: grid;
    grid-template-columns: 100px 1fr;
    gap: 8px 12px;
    font-size: 12px;

    dt {
      font-weight: 800;
      color: $p-ink-mid;
    }

    dd {
      margin: 0;
      color: $p-ink;
    }
  }

  &__settings-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  &__dist-summary {
    margin-bottom: 8px;
  }

  &__dist-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;

    @media (max-width: 768px) { grid-template-columns: 1fr; }
  }

  &__dist-item {
    padding: 12px 14px;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    background: $p-panel-alt;
  }

  &__dist-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
  }

  &__dist-count {
    text-align: right;

    p { margin: 0; font-size: 10px; color: $p-ink-soft; }
    strong { font-family: $p-font-mono; font-size: 14px; font-weight: 900; color: $p-ink; }
  }

  &__dist-levels {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 8px;
  }

  &__dist-bar {
    height: 6px;
    background: $p-line-soft;
    border-radius: 3px;
    overflow: hidden;
  }

  &__dist-fill {
    height: 100%;
    background: $p-point-deep;
    transition: width $p-dur $p-ease;
  }

  // ── Footer ──
  &__foot {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    padding: 14px 0;
  }

  // ── Reload modal ──
  &__reload-confirm {
    text-align: center;
    padding: 12px 8px;

    p { margin: 12px 0 0; font-size: 13px; color: $p-ink; line-height: 1.6; }
  }

  &__reload-icon {
    font-size: 48px !important;
    color: $p-warn;
  }
}

// Transitions
.step-fade-enter-active,
.step-fade-leave-active {
  transition: opacity 0.18s, transform 0.22s $p-ease;
}
.step-fade-enter-from,
.step-fade-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
